import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerCloseButton,
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { API, graphqlOperation } from "aws-amplify";
import { MdArrowBack, MdCheckCircle, MdOutlineAssignment } from "react-icons/md";
import { AiFillStar, AiOutlineStar } from "react-icons/ai";
import { BsChevronRight } from "react-icons/bs";
import {
  getFormDraft,
  listFormsForBooking,
  skipForm,
  submitFormResponse,
} from "../../Backend/Forms/formsApi";
import { getGuest } from "../../graphql/queries";
import {
  TRIGGER_LABELS,
  TRIGGER_ORDER,
  answersToMap,
  applyGuestPrefill,
  buildAnswerPayload,
  coerceConsent,
  coerceYesNo,
  getStatus,
  isEmailVerified,
  mapSubmitError,
  normalizeTrigger,
  normalizeVerifiedEmails,
  sortQuestions,
  validateAnswers,
} from "./Forms/formsLogic";
import { ACCENT, DANGER, fieldProps, invalidFieldProps } from "./Forms/formsTheme";
import { GuestListInput } from "./Forms/GuestListInput";
import { EmailVerifyInput } from "./Forms/EmailVerifyInput";

const AUTO_SAVE_MS = 5000;

const STATUS_STYLES = {
  not_started: { label: "Not started", bg: "whiteAlpha.100", color: "gray.300" },
  pending: { label: "Draft", bg: "yellow.900", color: "yellow.200" },
  submitted: { label: "Submitted", bg: "green.900", color: "green.200" },
  skipped: { label: "Skipped", bg: "whiteAlpha.100", color: "gray.400" },
};

const formatSubmittedAt = (iso) => {
  if (!iso) return "";
  try {
    return DateTime.fromISO(iso).toFormat("MMM d, yyyy 'at' h:mm a");
  } catch (e) {
    return "";
  }
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || STATUS_STYLES.not_started;
  return (
    <Badge
      bg={style.bg}
      color={style.color}
      fontSize="10px"
      fontWeight={700}
      textTransform="uppercase"
      letterSpacing="0.06em"
      px={2}
      py={0.5}
      borderRadius="full"
    >
      {style.label}
    </Badge>
  );
};

const StarRating = ({ value, max = 5, disabled, onChange }) => {
  const numeric = parseInt(value, 10);
  const current = Number.isFinite(numeric) ? numeric : 0;
  return (
    <Flex gap={1.5}>
      {Array.from({ length: max }).map((_, i) => {
        const starValue = i + 1;
        const filled = starValue <= current;
        return (
          <IconButton
            key={starValue}
            aria-label={`Rate ${starValue} of ${max}`}
            icon={
              <Icon
                as={filled ? AiFillStar : AiOutlineStar}
                fontSize="22px"
                color={filled ? "#FBBF24" : "whiteAlpha.500"}
              />
            }
            size="sm"
            variant="ghost"
            isDisabled={disabled}
            onClick={() => onChange(String(starValue))}
            _hover={{ bg: "whiteAlpha.100" }}
            _active={{ bg: "whiteAlpha.200" }}
          />
        );
      })}
    </Flex>
  );
};

const YesNoToggle = ({ value, disabled, onChange }) => {
  const current = coerceYesNo(value);
  const renderOption = (option, label) => {
    const active = current === option;
    return (
      <Button
        flex={1}
        h="44px"
        borderRadius="12px"
        fontSize="13px"
        fontWeight={700}
        bg={active ? ACCENT : "whiteAlpha.50"}
        color={active ? "white" : "gray.300"}
        border="1px solid"
        borderColor={active ? ACCENT : "whiteAlpha.200"}
        _hover={{ bg: active ? "#6e6be6" : "whiteAlpha.100" }}
        _active={{ bg: active ? "#5c59d4" : "whiteAlpha.200" }}
        isDisabled={disabled}
        onClick={() => onChange(option)}
      >
        {label}
      </Button>
    );
  };
  return (
    <Flex gap={2}>
      {renderOption("true", "Yes")}
      {renderOption("false", "No")}
    </Flex>
  );
};

const ChoiceList = ({ options = [], value, multi, disabled, onChange }) => {
  const selected = useMemo(() => {
    if (multi) return new Set(Array.isArray(value) ? value : []);
    return new Set(value ? [value] : []);
  }, [value, multi]);

  const toggle = (id) => {
    if (multi) {
      const next = new Set(selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onChange(Array.from(next));
    } else {
      onChange(id);
    }
  };

  return (
    <Stack spacing={2}>
      {options.map((opt) => {
        const active = selected.has(opt.id);
        return (
          <Button
            key={opt.id}
            justifyContent="flex-start"
            h="auto"
            minH="44px"
            py={3}
            px={3}
            whiteSpace="normal"
            textAlign="left"
            borderRadius="12px"
            fontSize="13px"
            fontWeight={600}
            bg={active ? "rgba(129, 126, 255, 0.18)" : "whiteAlpha.50"}
            color={active ? "white" : "gray.200"}
            border="1px solid"
            borderColor={active ? ACCENT : "whiteAlpha.200"}
            _hover={{ bg: active ? "rgba(129, 126, 255, 0.24)" : "whiteAlpha.100" }}
            _active={{ bg: "whiteAlpha.200" }}
            isDisabled={disabled}
            onClick={() => toggle(opt.id)}
            leftIcon={
              <Center
                w="18px"
                h="18px"
                borderRadius={multi ? "4px" : "full"}
                border="1.5px solid"
                borderColor={active ? ACCENT : "whiteAlpha.400"}
                bg={active ? ACCENT : "transparent"}
                flexShrink={0}
              >
                {active && (
                  <Icon as={MdCheckCircle} fontSize="12px" color="white" />
                )}
              </Center>
            }
          >
            {opt.label}
          </Button>
        );
      })}
    </Stack>
  );
};

const ConsentCheckbox = ({ question, value, disabled, invalid, onChange }) => (
  <Box
    p={3}
    borderRadius="12px"
    bg="whiteAlpha.50"
    border="1px solid"
    borderColor={invalid ? "rgba(248, 113, 113, 0.45)" : "whiteAlpha.100"}
  >
    <Checkbox
      isChecked={coerceConsent(value) === "true"}
      isDisabled={disabled}
      onChange={(e) => onChange(e.target.checked ? "true" : "")}
      alignItems="flex-start"
      sx={{
        ".chakra-checkbox__control": {
          mt: "2px",
          borderColor: "whiteAlpha.400",
          _checked: {
            bg: ACCENT,
            borderColor: ACCENT,
            color: "white",
          },
        },
      }}
    >
      <Text fontSize="13px" color="white" lineHeight="1.5">
        {question.prompt}
        <Text as="span" color={DANGER} ml={1}>
          *
        </Text>
      </Text>
    </Checkbox>
  </Box>
);

const QuestionInput = ({
  bookingId,
  formId,
  question,
  value,
  disabled,
  invalid,
  verified,
  guestRowErrors,
  onChange,
  onVerified,
}) => {
  const placeholder = question.placeholder || "";
  const inputProps = {
    ...fieldProps,
    ...(invalid ? invalidFieldProps : {}),
  };

  switch (question.type) {
    case "long_text":
      return (
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          isDisabled={disabled}
          minH="120px"
          {...inputProps}
        />
      );
    case "single_choice":
      return (
        <ChoiceList
          options={question.options}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      );
    case "multi_choice":
      return (
        <ChoiceList
          options={question.options}
          value={value}
          multi
          disabled={disabled}
          onChange={onChange}
        />
      );
    case "yes_no":
      return (
        <YesNoToggle value={value} disabled={disabled} onChange={onChange} />
      );
    case "rating":
      return (
        <StarRating
          value={value}
          max={question.maxRating || 5}
          disabled={disabled}
          onChange={onChange}
        />
      );
    case "number":
      return (
        <Input
          type="number"
          inputMode="decimal"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          isDisabled={disabled}
          h="46px"
          {...inputProps}
        />
      );
    case "date":
      return (
        <Input
          type="date"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          isDisabled={disabled}
          h="46px"
          {...inputProps}
        />
      );
    case "email":
      if (question.requireVerification) {
        return (
          <EmailVerifyInput
            bookingId={bookingId}
            formId={formId}
            question={question}
            value={value}
            disabled={disabled}
            invalid={invalid}
            verified={verified}
            onChange={onChange}
            onVerified={onVerified}
          />
        );
      }
      return (
        <Input
          type="email"
          inputMode="email"
          autoComplete="email"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "name@email.com"}
          isDisabled={disabled}
          h="46px"
          {...inputProps}
        />
      );
    case "phone":
      return (
        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "+1 555 123 4567"}
          isDisabled={disabled}
          h="46px"
          {...inputProps}
        />
      );
    case "consent_checkbox":
      return (
        <ConsentCheckbox
          question={question}
          value={value}
          disabled={disabled}
          invalid={invalid}
          onChange={onChange}
        />
      );
    case "guest_list":
      return (
        <GuestListInput
          value={value}
          disabled={disabled}
          rowErrors={guestRowErrors}
          onChange={onChange}
        />
      );
    case "short_text":
    default:
      return (
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          isDisabled={disabled}
          h="46px"
          {...inputProps}
        />
      );
  }
};

const RequiredBadge = ({ size = "sm" }) => (
  <Badge
    bg="rgba(248, 113, 113, 0.15)"
    color={DANGER}
    border="1px solid rgba(248, 113, 113, 0.35)"
    fontSize={size === "sm" ? "10px" : "11px"}
    fontWeight={700}
    textTransform="uppercase"
    letterSpacing="0.06em"
    px={2}
    py={0.5}
    borderRadius="full"
  >
    Required
  </Badge>
);

const FormCard = ({ form, response, onSelect }) => {
  const status = getStatus(response);
  const questionCount = form.questions?.length || 0;
  const isRequired = !!form.isRequired;
  return (
    <Flex
      as="button"
      align="center"
      gap={3}
      p={3}
      borderRadius="14px"
      bg={isRequired ? "rgba(248, 113, 113, 0.06)" : "whiteAlpha.50"}
      border="1px solid"
      borderColor={isRequired ? "rgba(248, 113, 113, 0.28)" : "whiteAlpha.100"}
      _hover={{
        bg: isRequired ? "rgba(248, 113, 113, 0.10)" : "whiteAlpha.100",
      }}
      _active={{ bg: "whiteAlpha.200", transform: "scale(0.99)" }}
      transition="all 0.15s ease"
      textAlign="left"
      onClick={onSelect}
      w="full"
    >
      <Center
        w="36px"
        h="36px"
        borderRadius="10px"
        bg={isRequired ? "rgba(248, 113, 113, 0.15)" : "whiteAlpha.100"}
        flexShrink={0}
      >
        <Icon
          as={MdOutlineAssignment}
          fontSize="16px"
          color={isRequired ? DANGER : ACCENT}
        />
      </Center>
      <Box flex="1" minW={0}>
        <Flex align="center" gap={2} mb={0.5}>
          <Text fontSize="13px" fontWeight={isRequired ? 800 : 700} noOfLines={1} color="white">
            {form.name}
            {isRequired && (
              <Text as="span" color={DANGER} ml={1}>
                *
              </Text>
            )}
          </Text>
        </Flex>
        {form.description && (
          <Text fontSize="11px" color="gray.400" lineHeight="1.4" noOfLines={2}>
            {form.description}
          </Text>
        )}
        <Flex align="center" gap={2} mt={1.5} flexWrap="wrap">
          <StatusBadge status={status} />
          {isRequired && <RequiredBadge />}
          <Text fontSize="10px" color="gray.500">
            {questionCount} {questionCount === 1 ? "question" : "questions"}
          </Text>
        </Flex>
      </Box>
      <Icon as={BsChevronRight} fontSize="14px" color="whiteAlpha.500" />
    </Flex>
  );
};

const FormEditor = ({
  bookingId,
  guestId,
  roomId,
  guest,
  form,
  response,
  onClose,
  onUpdated,
  onStaleForm,
}) => {
  // Chakra defaults toasts to the bottom of the viewport, which put them
  // directly over the Submit / Continue buttons and blocked the guest from
  // advancing right after finishing a form. Pin them top-centre and keep the
  // container narrow so they read as a compact banner rather than a bar.
  const toast = useToast({
    position: "top",
    variant: "subtle",
    containerStyle: {
      maxWidth: "min(360px, 88vw)",
      marginTop: "max(8px, env(safe-area-inset-top))",
      fontSize: "13px",
    },
  });

  const sortedQuestions = useMemo(
    () => sortQuestions(form.questions),
    [form.questions]
  );

  // Deliberately independent of `guest`: the guest record can land after
  // the editor mounts, and folding it in here would re-seed state (and
  // discard in-progress typing). Prefill gets its own effect below.
  const initialAnswers = useMemo(
    () => answersToMap(response?.answers, sortedQuestions),
    [response, sortedQuestions]
  );
  const initialVerified = useMemo(
    () => normalizeVerifiedEmails(response?.verifiedEmails),
    [response]
  );

  const [answers, setAnswers] = useState(initialAnswers);
  const [verifiedEmails, setVerifiedEmails] = useState(initialVerified);
  const [errors, setErrors] = useState({});
  const [guestListErrors, setGuestListErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [autoSavedAt, setAutoSavedAt] = useState(null);

  const dirtyRef = useRef(false);
  const lastSavedRef = useRef(JSON.stringify(initialAnswers));
  // Set once the response reaches a terminal state so the unmount flush
  // can't overwrite a submission with a trailing draft.
  const finalizedRef = useRef(false);
  const flushRef = useRef(null);
  const questionRefs = useRef({});

  const status = getStatus(response);
  // Only submitted responses are read-only. Skipped forms stay reopenable.
  const isLocked = status === "submitted";
  const wasSkipped = status === "skipped";
  const isRequired = !!form.isRequired;
  const busy = submitting || skipping;

  // Read through a ref so seeding answers can apply guest prefill without
  // the guest record's arrival re-triggering the fetches below.
  const guestRef = useRef(guest);
  useEffect(() => {
    guestRef.current = guest;
  }, [guest]);

  const seedAnswers = useCallback(
    (map) => applyGuestPrefill(map, sortedQuestions, guestRef.current),
    [sortedQuestions]
  );

  useEffect(() => {
    if (dirtyRef.current) return;
    setAnswers(seedAnswers(initialAnswers));
    setVerifiedEmails(initialVerified);
    lastSavedRef.current = JSON.stringify(initialAnswers);
    setErrors({});
    setGuestListErrors({});
  }, [initialAnswers, initialVerified, seedAnswers]);

  // Rehydrate against the dedicated draft endpoint so a draft saved on
  // another device wins over the (possibly older) list payload. A 404 just
  // means the guest hasn't started, and anything already typed is kept.
  useEffect(() => {
    if (!bookingId || !form?.id || isLocked) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getFormDraft(bookingId, form.id);
        const saved = res?.data;
        if (cancelled || !saved || dirtyRef.current) return;
        const restored = answersToMap(saved.answers, sortedQuestions);
        setAnswers(seedAnswers(restored));
        setVerifiedEmails(normalizeVerifiedEmails(saved.verifiedEmails));
        lastSavedRef.current = JSON.stringify(restored);
      } catch (err) {
        // No saved draft yet — the common first-open case.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId, form?.id, sortedQuestions, isLocked, seedAnswers]);

  // Seed guest_list rows once the primary guest's details are known. Only
  // touches rows that are still empty, and returns the same object when
  // there's nothing to fill, so typing and saved drafts are never clobbered.
  useEffect(() => {
    if (!guest || isLocked) return;
    setAnswers((prev) => applyGuestPrefill(prev, sortedQuestions, guest));
  }, [guest, sortedQuestions, isLocked]);

  const saveDraft = useCallback(
    (snapshot) =>
      submitFormResponse(bookingId, {
        formID: form.id,
        status: "pending",
        answers: buildAnswerPayload(snapshot, sortedQuestions),
        guestID: guestId,
        roomID: roomId,
      }),
    [bookingId, form.id, sortedQuestions, guestId, roomId]
  );

  useEffect(() => {
    if (isLocked) return undefined;
    if (!dirtyRef.current) return undefined;
    const handle = setTimeout(async () => {
      const snapshot = JSON.stringify(answers);
      if (snapshot === lastSavedRef.current) return;
      try {
        await saveDraft(answers);
        lastSavedRef.current = snapshot;
        setAutoSavedAt(new Date());
      } catch (e) {
        // Silent autosave failure; the guest can still submit manually.
        console.warn("Form autosave failed:", e?.message || e);
      }
    }, AUTO_SAVE_MS);
    return () => clearTimeout(handle);
  }, [answers, isLocked, saveDraft]);

  // Save whatever is typed when the guest navigates away mid-form.
  useEffect(() => {
    flushRef.current = () => {
      if (isLocked || finalizedRef.current || !dirtyRef.current) return;
      const snapshot = JSON.stringify(answers);
      if (snapshot === lastSavedRef.current) return;
      lastSavedRef.current = snapshot;
      saveDraft(answers).catch((e) =>
        console.warn("Form draft flush failed:", e?.message || e)
      );
    };
  }, [answers, isLocked, saveDraft]);

  useEffect(() => () => flushRef.current?.(), []);

  const clearError = (questionId) => {
    setErrors((prev) => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
    setGuestListErrors((prev) => {
      if (!prev[questionId]) return prev;
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  };

  const handleChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    dirtyRef.current = true;
    clearError(questionId);
  };

  const handleVerified = (questionId, email) => {
    setVerifiedEmails((prev) => ({ ...prev, [questionId]: email }));
    setAnswers((prev) => ({ ...prev, [questionId]: email }));
    dirtyRef.current = true;
    clearError(questionId);
  };

  const focusQuestion = (questionId) => {
    const node = questionRefs.current[questionId];
    node?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };

  const handleSubmit = async () => {
    if (isLocked) return;

    const validation = validateAnswers({
      questions: sortedQuestions,
      answers,
      verifiedEmails,
    });
    if (!validation.isValid) {
      setErrors(validation.errors);
      setGuestListErrors(validation.guestListErrors);
      focusQuestion(validation.firstErrorId);
      toast({
        title: "Almost there",
        description: "Please check the highlighted questions.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      // Omitting `status` submits — the backend defaults it.
      const res = await submitFormResponse(bookingId, {
        formID: form.id,
        answers: buildAnswerPayload(answers, sortedQuestions),
        guestID: guestId,
        roomID: roomId,
      });
      finalizedRef.current = true;
      dirtyRef.current = false;
      // Title only — the guest's next action is the Continue button, so this
      // stays a one-line confirmation rather than a two-line block.
      toast({
        title: "Form submitted",
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      onUpdated?.({ formId: form.id, response: res?.data });
    } catch (error) {
      const mapped = mapSubmitError(error, sortedQuestions);
      setErrors(mapped.errors);
      setGuestListErrors(mapped.guestListErrors);
      if (mapped.focusId) focusQuestion(mapped.focusId);
      if (mapped.shouldRefetch) onStaleForm?.();
      toast({
        title: "Couldn't submit",
        description: mapped.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (isLocked || isRequired) return;
    setSkipping(true);
    try {
      const res = await skipForm(bookingId, {
        formID: form.id,
        guestID: guestId,
        roomID: roomId,
      });
      finalizedRef.current = true;
      dirtyRef.current = false;
      toast({
        title: "Form skipped",
        status: "info",
        duration: 2500,
        isClosable: true,
      });
      onUpdated?.({ formId: form.id, response: res?.data });
    } catch (error) {
      toast({
        title: "Couldn't skip",
        description:
          error?.response?.data?.error ||
          error?.message ||
          "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSkipping(false);
    }
  };

  return (
    <>
      <Flex
        px={4}
        py={3}
        align="center"
        gap={2}
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
      >
        <IconButton
          aria-label="Back"
          icon={<Icon as={MdArrowBack} fontSize="18px" />}
          variant="ghost"
          size="sm"
          color="white"
          _hover={{ bg: "whiteAlpha.100" }}
          onClick={onClose}
        />
        <Box flex="1" minW={0}>
          <Text fontSize="15px" fontWeight={700} noOfLines={1}>
            {form.name}
          </Text>
          {isLocked && response?.submittedAt && (
            <Text fontSize="11px" color="gray.500">
              Submitted {formatSubmittedAt(response.submittedAt)}
            </Text>
          )}
          {!isLocked && autoSavedAt && (
            <Text fontSize="11px" color="gray.500">
              Draft saved
            </Text>
          )}
        </Box>
        <StatusBadge status={status} />
      </Flex>

      <DrawerBody px={{ base: 4, md: 6 }} pt={4} pb={6}>
        {isRequired && !isLocked && (
          <Flex mb={3}>
            <RequiredBadge />
          </Flex>
        )}
        {form.description && (
          <Text fontSize="13px" color="gray.400" mb={4} lineHeight="1.5">
            {form.description}
          </Text>
        )}

        {isLocked && (
          <Alert
            status="success"
            borderRadius="12px"
            mb={4}
            bg="green.900"
            color="white"
          >
            <AlertIcon color="green.300" />
            <Text fontSize="13px">
              Your responses are locked. Reach out to your host if anything
              needs to change.
            </Text>
          </Alert>
        )}

        {wasSkipped && (
          <Alert
            status="info"
            borderRadius="12px"
            mb={4}
            bg="whiteAlpha.100"
            color="white"
          >
            <AlertIcon color="whiteAlpha.700" />
            <Text fontSize="13px">
              You skipped this form. You can still fill it out and submit it.
            </Text>
          </Alert>
        )}

        <Stack spacing={5}>
          {sortedQuestions.map((q) => {
            const errorMsg = errors[q.id];
            const isConsent = q.type === "consent_checkbox";
            return (
              <Box
                key={q.id}
                ref={(node) => {
                  questionRefs.current[q.id] = node;
                }}
              >
                {!isConsent && (
                  <>
                    <Text
                      fontSize="13px"
                      fontWeight={700}
                      color="white"
                      mb={q.helpText ? 0.5 : 1.5}
                    >
                      {q.prompt}
                      {q.required && (
                        <Text as="span" color={DANGER} ml={1}>
                          *
                        </Text>
                      )}
                    </Text>
                    {q.helpText && (
                      <Text fontSize="11px" color="gray.500" mb={2}>
                        {q.helpText}
                      </Text>
                    )}
                  </>
                )}
                <QuestionInput
                  bookingId={bookingId}
                  formId={form.id}
                  question={q}
                  value={answers[q.id]}
                  disabled={isLocked || busy}
                  invalid={!!errorMsg}
                  verified={isEmailVerified(q, answers[q.id], verifiedEmails)}
                  guestRowErrors={guestListErrors[q.id]}
                  onChange={(v) => handleChange(q.id, v)}
                  onVerified={(email) => handleVerified(q.id, email)}
                />
                {isConsent && q.helpText && (
                  <Text fontSize="11px" color="gray.500" mt={1.5}>
                    {q.helpText}
                  </Text>
                )}
                {errorMsg && (
                  <Text fontSize="11px" color={DANGER} mt={1.5}>
                    {errorMsg}
                  </Text>
                )}
              </Box>
            );
          })}
        </Stack>
      </DrawerBody>

      {!isLocked && (
        <Box
          px={4}
          pt={3}
          pb={4}
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}
          borderTop="1px solid"
          borderColor="whiteAlpha.100"
          bg="gray.900"
          flexShrink={0}
        >
          <Stack spacing={2}>
            <Button
              onClick={handleSubmit}
              isLoading={submitting}
              loadingText="Submitting..."
              isDisabled={skipping}
              bg={ACCENT}
              color="white"
              h="48px"
              borderRadius="14px"
              fontSize="14px"
              fontWeight={700}
              _hover={{ bg: "#6e6be6" }}
              _active={{ bg: "#5c59d4" }}
            >
              Submit
            </Button>
            {!isRequired && !wasSkipped && (
              <Button
                onClick={handleSkip}
                isLoading={skipping}
                isDisabled={submitting}
                variant="ghost"
                color="whiteAlpha.700"
                h="42px"
                borderRadius="12px"
                fontSize="13px"
                fontWeight={600}
                _hover={{ bg: "whiteAlpha.100" }}
              >
                Skip for now
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </>
  );
};

const FormsList = ({ formsData, onSelect, onDone }) => {
  const grouped = useMemo(() => {
    const map = {};
    for (const item of formsData) {
      const trigger = normalizeTrigger(item?.form?.trigger);
      if (!map[trigger]) map[trigger] = [];
      map[trigger].push(item);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => {
        const aReq = a?.form?.isRequired ? 1 : 0;
        const bReq = b?.form?.isRequired ? 1 : 0;
        return bReq - aReq;
      });
    }
    return map;
  }, [formsData]);

  const sections = TRIGGER_ORDER.filter((t) => grouped[t]?.length).concat(
    Object.keys(grouped).filter((t) => !TRIGGER_ORDER.includes(t))
  );

  const allCompleted = useMemo(() => {
    if (!formsData.length) return false;
    return formsData.every((it) => {
      const status = it?.response?.status;
      if (it?.form?.isRequired) return status === "submitted";
      return status === "submitted" || status === "skipped";
    });
  }, [formsData]);

  return (
    <>
      <DrawerBody px={{ base: 4, md: 6 }} pt={2} pb={6}>
        <Text fontSize="13px" color="gray.400" mb={4} lineHeight="1.5">
          {allCompleted
            ? "You're all caught up. Tap any form to review your answers."
            : "Your host has a few quick questions. Your answers are saved as you type."}
        </Text>
        <Stack spacing={5}>
          {sections.map((trigger) => (
            <Box key={trigger}>
              <Text
                fontSize="10px"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing="0.06em"
                color="gray.500"
                mb={2}
              >
                {TRIGGER_LABELS[trigger] || trigger}
              </Text>
              <Stack spacing={2.5}>
                {grouped[trigger].map(({ form, response }) => (
                  <FormCard
                    key={form.id}
                    form={form}
                    response={response}
                    onSelect={() => onSelect(form.id)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </DrawerBody>

      {allCompleted && (
        <Box
          px={4}
          pt={3}
          pb={4}
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)" }}
          borderTop="1px solid"
          borderColor="whiteAlpha.100"
          bg="gray.900"
          flexShrink={0}
        >
          <Button
            onClick={onDone}
            bg={ACCENT}
            color="white"
            w="full"
            h="48px"
            borderRadius="14px"
            fontSize="14px"
            fontWeight={700}
            leftIcon={<Icon as={MdCheckCircle} fontSize="18px" />}
            _hover={{ bg: "#6e6be6" }}
            _active={{ bg: "#5c59d4" }}
          >
            Done
          </Button>
        </Box>
      )}
    </>
  );
};

const EmptyState = () => (
  <DrawerBody px={4} py={10}>
    <Center flexDirection="column" textAlign="center" gap={3} py={10}>
      <Center w="56px" h="56px" borderRadius="full" bg="whiteAlpha.100">
        <Icon as={MdOutlineAssignment} fontSize="24px" color={ACCENT} />
      </Center>
      <Text fontSize="15px" fontWeight={700} color="white">
        No forms yet
      </Text>
      <Text fontSize="13px" color="gray.400" maxW="260px">
        Your host hasn't added any forms for this stay. You're all good!
      </Text>
    </Center>
  </DrawerBody>
);

const ErrorState = ({ message, onRetry }) => (
  <DrawerBody px={4} py={10}>
    <Center flexDirection="column" textAlign="center" gap={3} py={10}>
      <Text fontSize="15px" fontWeight={700} color="white">
        Couldn't load forms
      </Text>
      <Text fontSize="13px" color="gray.400" maxW="260px">
        {message || "Something went wrong. Please try again."}
      </Text>
      <Button
        mt={2}
        onClick={onRetry}
        bg={ACCENT}
        color="white"
        h="40px"
        px={5}
        borderRadius="12px"
        fontSize="13px"
        _hover={{ bg: "#6e6be6" }}
      >
        Retry
      </Button>
    </Center>
  </DrawerBody>
);

export const FormsDrawer = ({
  isOpen,
  onClose,
  bookingId,
  guestId,
  roomId,
  guest: guestProp,
  // Optional `check_in` / `check_out` / `general` (or array) to scope the
  // drawer to one stage of the stay. Unset shows every form, grouped.
  triggerFilter,
}) => {
  const [loading, setLoading] = useState(false);
  const [formsData, setFormsData] = useState([]);
  const [error, setError] = useState(null);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [guestRecord, setGuestRecord] = useState(null);

  const fetchForms = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listFormsForBooking(bookingId);
      const items = res?.data?.items || res?.data?.forms || [];
      setFormsData(Array.isArray(items) ? items : []);
    } catch (e) {
      console.error("Failed to load forms:", e);
      setError(
        e?.response?.data?.error ||
          e?.message ||
          "Couldn't load forms right now."
      );
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormId(null);
      fetchForms();
    }
  }, [isOpen, fetchForms]);

  const allowedTriggers = useMemo(() => {
    if (!triggerFilter) return null;
    const list = Array.isArray(triggerFilter) ? triggerFilter : [triggerFilter];
    return new Set(list.map(normalizeTrigger));
  }, [triggerFilter]);

  const visibleForms = useMemo(
    () =>
      formsData.filter((item) => {
        const form = item?.form;
        if (!form?.id || form.isActive === false) return false;
        if (!allowedTriggers) return true;
        return allowedTriggers.has(normalizeTrigger(form.trigger));
      }),
    [formsData, allowedTriggers]
  );

  // Guest details pre-fill the first row of guest_list questions, so they're
  // only worth fetching when a visible form actually has one.
  const needsGuestDetails = useMemo(
    () =>
      visibleForms.some((item) =>
        (item?.form?.questions || []).some((q) => q?.type === "guest_list")
      ),
    [visibleForms]
  );

  useEffect(() => {
    if (!isOpen || guestProp || guestRecord || !guestId || !needsGuestDetails) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await API.graphql(
          graphqlOperation(getGuest, { id: guestId })
        );
        if (!cancelled) setGuestRecord(res?.data?.getGuest || null);
      } catch (err) {
        // Prefill is a convenience; the guest can still type their details.
        console.warn(
          "Couldn't load guest for form prefill:",
          err?.message || err
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, guestProp, guestRecord, guestId, needsGuestDetails]);

  const guest = guestProp || guestRecord;

  const selected = useMemo(
    () => visibleForms.find((f) => f?.form?.id === selectedFormId) || null,
    [visibleForms, selectedFormId]
  );

  const handleEditorUpdated = ({ formId, response }) => {
    setFormsData((prev) =>
      prev.map((item) =>
        item?.form?.id === formId ? { ...item, response } : item
      )
    );
    setSelectedFormId(null);
  };

  // The form definition changed under us (FORM_NOT_FOUND / UNKNOWN_QUESTION).
  const handleStaleForm = useCallback(() => {
    setSelectedFormId(null);
    fetchForms();
  }, [fetchForms]);

  const renderBody = () => {
    if (loading) {
      return (
        <DrawerBody px={4} py={10}>
          <Center py={12}>
            <Spinner color={ACCENT} />
          </Center>
        </DrawerBody>
      );
    }
    if (error) {
      return <ErrorState message={error} onRetry={fetchForms} />;
    }
    if (selected) {
      return (
        <FormEditor
          // Remount per form so no answer state survives switching forms.
          key={selected.form.id}
          bookingId={bookingId}
          guestId={guestId}
          roomId={roomId}
          guest={guest}
          form={selected.form}
          response={selected.response}
          onClose={() => setSelectedFormId(null)}
          onUpdated={handleEditorUpdated}
          onStaleForm={handleStaleForm}
        />
      );
    }
    if (!visibleForms.length) {
      return <EmptyState />;
    }
    return (
      <FormsList
        formsData={visibleForms}
        onSelect={(id) => setSelectedFormId(id)}
        onDone={onClose}
      />
    );
  };

  return (
    <Drawer placement="bottom" isOpen={isOpen} onClose={onClose}>
      <DrawerOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <DrawerContent
        h="min(85vh, 85dvh)"
        maxH="min(85vh, 85dvh)"
        borderTopRadius="20px"
        bg="#1A202C"
        color="white"
        display="flex"
        flexDirection="column"
        overflow="hidden"
        maxW={{ base: "100%", md: "640px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex justify="center" pt={3} pb={1}>
          <Box w="36px" h="4px" borderRadius="full" bg="whiteAlpha.300" />
        </Flex>

        {!selected && (
          <Flex
            px={4}
            py={3}
            align="center"
            justify="space-between"
            borderBottom="1px solid"
            borderColor="whiteAlpha.100"
          >
            <Box>
              <Text
                fontSize="11px"
                fontWeight={600}
                textTransform="uppercase"
                letterSpacing="0.08em"
                color="gray.500"
              >
                From your host
              </Text>
              <Text fontSize="18px" fontWeight={800} letterSpacing="-0.02em">
                Forms
              </Text>
            </Box>
            <DrawerCloseButton
              pos="relative"
              top={0}
              right={0}
              borderRadius="full"
              bg="whiteAlpha.100"
              _hover={{ bg: "whiteAlpha.200" }}
              size="sm"
            />
          </Flex>
        )}

        {renderBody()}
      </DrawerContent>
    </Drawer>
  );
};

export default FormsDrawer;
